import type { PickedRecord } from "@components/ClientRecordPicker/ClientRecordPicker";
import type { FormVariable } from "./api/useProjectFormOptions";
import type { ProjectAddress, VariableValue } from "./types";

export interface LocationDraft extends ProjectAddress {
  picked: PickedRecord | null;
}

export const EMPTY_LOCATION: LocationDraft = {
  name: "",
  street: "",
  street2: "",
  city: "",
  state: "",
  postalCode: "",
  countryId: "",
  remote: false,
  picked: null,
};

const ADDRESS_FIELDS: (keyof ProjectAddress)[] = [
  "name",
  "street",
  "street2",
  "city",
  "state",
  "postalCode",
  "countryId",
  "remote",
];

// A location counts as entered while any part of it is filled in, so one the
// user started and then cleared is optional again.
export const isLocationEntered = (location: LocationDraft): boolean =>
  location.remote ||
  location.picked !== null ||
  [
    location.name,
    location.street,
    location.street2,
    location.city,
    location.state,
    location.postalCode,
  ].some((part) => part.trim() !== "");

// Whether the address differs from the saved one, or a site was picked for it.
export const isAddressChanged = (
  location: LocationDraft,
  saved: ProjectAddress
): boolean =>
  location.picked !== null ||
  ADDRESS_FIELDS.some((field) => location[field] !== saved[field]);

export const toVariableValues = (
  variables?: { name: string; value?: string | null }[]
): VariableValue[] =>
  (variables ?? []).map(({ name, value }) => ({ name, value: value ?? "" }));

// Takes the value of each variable the list names.
export const fillVariables = (
  variables: FormVariable[],
  values: VariableValue[]
): FormVariable[] =>
  variables.map((v) => {
    const match = values.find((s) => s.name === v.name);
    return match ? { ...v, value: match.value ?? "" } : v;
  });

// An empty select starts on its default option, so the default is saved.
export const withSelectDefaults = (variables: FormVariable[]): FormVariable[] =>
  variables.map((v) => ({
    ...v,
    value: v.value || (v.select_options.find((o) => o.default)?.value ?? ""),
  }));

export const variableLengthError = (
  variables: FormVariable[]
): { kind: "min" | "max"; limit: number } | null => {
  for (const variable of variables) {
    if (
      variable["variable-type"] !== "text" ||
      variable.select_options.length > 0 ||
      !variable.value
    ) {
      continue;
    }
    if (variable.minimum != null && variable.value.length < variable.minimum) {
      return { kind: "min", limit: variable.minimum };
    }
    if (variable.maximum != null && variable.value.length > variable.maximum) {
      return { kind: "max", limit: variable.maximum };
    }
  }
  return null;
};
